import React, { useState, useEffect } from "react";
import { api } from "../../api";
import { motion } from "framer-motion";

export default function ManageCards() {
  const [cardNumber, setCardNumber] = useState("");
  const [roomId, setRoomId] = useState("");

  const [cardError, setCardError] = useState("");
  const [cardLoading, setCardLoading] = useState(false);
  const [cardSuccess, setCardSuccess] = useState("");

  const [forcedNumbers, setForcedNumbers] = useState([]);

  // ✅ BOT STATES
  const [botEnabled, setBotEnabled] = useState(true);
  const [botLoading, setBotLoading] = useState(false);
  const [botMessage, setBotMessage] = useState("");

  // 📢 BROADCAST STATES
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastRole, setBroadcastRole] = useState("");
  const [minBalance, setMinBalance] = useState("");

  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [broadcastResponse, setBroadcastResponse] = useState("");
  const [broadcastError, setBroadcastError] = useState("");
  /* =========================
     🎯 FORCE WIN CARD
  ========================= */
  const selectCard = async () => {
    if (!cardNumber) {
      setCardError("Please enter a card number");
      return;
    }

    if (!roomId) {
      setCardError("Please select a room");
      return;
    }

    setCardError("");
    setCardSuccess("");
    setCardLoading(true);

    try {
      const res = await api.post("/admin/force-win", {
        roomId,
        cardNumber: Number(cardNumber),
      });

      setCardSuccess("✅ Forced winner applied successfully");
      setForcedNumbers(res.data.forcedWinningNumbers || []);
      setCardNumber("");
    } catch (err) {
      setCardError(err.response?.data?.error || "Failed to set forced winner");
    } finally {
      setCardLoading(false);
    }
  };

  /* =========================
     🤖 FETCH BOT STATUS
  ========================= */
  const fetchBotStatus = async () => {
    if (!roomId) return;

    try {
      const res = await api.get(`/admin/bot/status?roomId=${roomId}`);
      setBotEnabled(res.data.isEnabled);
    } catch (err) {
      console.error("Failed to fetch bot status");
    }
  };

  useEffect(() => {
    fetchBotStatus();
  }, [roomId]);

  /* =========================
     🤖 TOGGLE BOT
  ========================= */
  const toggleBot = async () => {
    if (!roomId) {
      setBotMessage("⚠️ Select a room first");
      return;
    }

    setBotLoading(true);
    setBotMessage("");

    try {
      const res = await api.post("/admin/bot/toggle", {
        roomId,
        isEnabled: !botEnabled,
      });

      setBotEnabled(!botEnabled);
      setBotMessage(res.data.message);
    } catch (err) {
      setBotMessage("❌ Failed to update bot");
    } finally {
      setBotLoading(false);
    }
  };

  /* =========================
     📢 BROADCAST PROMOTION
  ========================= */
  const sendBroadcast = async () => {
    if (!broadcastMessage) {
      setBroadcastError("Message is required");
      return;
    }

    setBroadcastLoading(true);
    setBroadcastError("");
    setBroadcastResponse("");

    try {
      const res = await api.post("/admin/broadcast", {
        message: broadcastMessage,
        role: broadcastRole || undefined,
        minBalance: minBalance ? Number(minBalance) : undefined,
      });

      setBroadcastResponse("✅ Broadcast sent successfully");
      setBroadcastMessage("");
      setBroadcastRole("");
      setMinBalance("");
    } catch (err) {
      setBroadcastError(
        err.response?.data?.message || "Failed to send broadcast",
      );
    } finally {
      setBroadcastLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b253d] px-4 py-10">
      <h1 className="text-center text-3xl sm:text-4xl text-white font-serif mb-8">
        Manage Bingo Card
      </h1>

      <div className="max-w-lg mx-auto bg-[#12385d] p-6 rounded-xl shadow-lg mt-6">
        <label className="text-gray-200 font-serif mb-2 block">
          Winner Card
        </label>

        {/* ROOM SELECT */}
        <select
          value={roomId}
          onChange={(e) => setRoomId(e.target.value)}
          className="w-full mb-3 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 rounded-md"
        >
          <option value="">Select Room</option>
          <option value="room1">ROOM_1</option>
          <option value="room2">ROOM_2</option>
          <option value="room3">ROOM_3</option>
          <option value="room4">ROOM_4</option>
          <option value="room5">ROOM_5</option>
          <option value="room6">ROOM_6</option>
          <option value="room7">ROOM_7</option>
          <option value="room8">ROOM_8</option>
          <option value="room9">ROOM_9</option>
        </select>

        {/* =========================
            🤖 BOT CONTROL UI
        ========================= */}
        <div className="mb-4 bg-[#0e2b48] p-4 rounded-md">
          <div className="flex justify-between items-center">
            <span className="text-white font-semibold">Bot Control</span>

            <button
              onClick={toggleBot}
              disabled={botLoading}
              className={`px-4 py-2 rounded-md font-semibold transition ${
                botEnabled
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {botLoading ? "Loading..." : botEnabled ? "ON" : "OFF"}
            </button>
          </div>

          <p className="text-gray-400 text-sm mt-2">
            Status:{" "}
            <span className={botEnabled ? "text-green-400" : "text-red-400"}>
              {botEnabled ? "Enabled" : "Disabled"}
            </span>
          </p>

          {botMessage && (
            <p className="text-blue-300 text-sm mt-2">{botMessage}</p>
          )}
        </div>

        {/* INPUT + BUTTON */}
        <div className="flex gap-2">
          <input
            type="number"
            placeholder="Card Number"
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            className="flex-1 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 rounded-md"
          />

          <button
            onClick={selectCard}
            disabled={cardLoading}
            className="bg-gradient-to-r from-[#4e6d8c] to-[#6784a4] text-white px-4 py-3 rounded-md font-semibold"
          >
            {cardLoading ? "Setting..." : "Win"}
          </button>
        </div>

        {cardError && <p className="text-red-400 mt-2">{cardError}</p>}
        {cardSuccess && <p className="text-green-400 mt-2">{cardSuccess}</p>}

        {/* FORCED NUMBERS */}
        {forcedNumbers.length > 0 && (
          <div className="mt-4 bg-[#0e2b48] p-3 rounded-md">
            <p className="text-gray-300 text-sm mb-2">
              Forced Winning Numbers:
            </p>
            <div className="flex flex-wrap gap-2">
              {forcedNumbers.map((num, i) => (
                <span
                  key={i}
                  className="bg-green-600 px-2 py-1 rounded text-white text-sm"
                >
                  {num}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* =========================
    📢 BROADCAST PROMOTION UI
========================= */}
      <div className="max-w-lg mx-auto bg-[#12385d] p-6 rounded-xl shadow-lg mt-6">
        <h2 className="text-white text-xl font-semibold mb-4">
          Broadcast Promotion
        </h2>

        {/* MESSAGE */}
        <textarea
          rows={6}
          placeholder="Enter promotion message..."
          value={broadcastMessage}
          onChange={(e) => setBroadcastMessage(e.target.value)}
          className="w-full mb-3 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 rounded-md"
        />

        {/* ROLE */}
        <input
          type="text"
          placeholder="Target Role (optional)"
          value={broadcastRole}
          onChange={(e) => setBroadcastRole(e.target.value)}
          className="w-full mb-3 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 rounded-md"
        />

        {/* MIN BALANCE */}
        <input
          type="text"
          placeholder="Minimum Balance (optional)"
          value={minBalance}
          onChange={(e) => setMinBalance(e.target.value)}
          className="w-full mb-3 bg-[#0e2b48] border border-[#2c4e74] text-white px-3 py-3 rounded-md"
        />

        {/* BUTTON */}
        <button
          onClick={sendBroadcast}
          disabled={broadcastLoading}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-md font-semibold"
        >
          {broadcastLoading ? "Sending..." : "Send Broadcast"}
        </button>

        {/* RESPONSE */}
        {broadcastError && (
          <p className="text-red-400 mt-2">{broadcastError}</p>
        )}
        {broadcastResponse && (
          <p className="text-green-400 mt-2">{broadcastResponse}</p>
        )}
      </div>
    </div>
  );
}
